import { Link } from "react-router-dom";
import { useState } from "react";
import { useForm, useController } from "react-hook-form";
import { IMaskInput } from "react-imask";
import IMask from "imask";
import CanvasParticles from "../components/CanvasParticles";

type FormData = {
  name: string;
  email: string;
  date_of_transaction: string;
  amount: string;
  currency: string;
  card_first6: string;
  card_last4: string;
  description: string;
};

const currentYear = new Date().getFullYear();

export default function DisputeResolutionFormPage() {
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ mode: "onTouched" });

  const { field: dateField } = useController({
    name: "date_of_transaction",
    control,
    rules: {
      required: "Date is required",
      validate: (value) => {
        if (!value || value.replace(/\D/g, "").length < 8) return "Enter a complete date";
        const [dd, mm, yyyy] = value.split("/").map(Number);
        const entered = new Date(yyyy, mm - 1, dd);
        if (isNaN(entered.getTime())) return "Invalid date";
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        if (entered > today) return "Date cannot be in the future";
        return true;
      },
    },
  });

  const { field: first6Field } = useController({
    name: "card_first6",
    control,
    rules: {
      required: "Required",
      validate: (v) => v.replace(/\D/g, "").length === 6 || "Must be 6 digits",
    },
  });

  const { field: last4Field } = useController({
    name: "card_last4",
    control,
    rules: {
      required: "Required",
      validate: (v) => v.replace(/\D/g, "").length === 4 || "Must be 4 digits",
    },
  });

  const { field: amountField } = useController({
    name: "amount",
    control,
    rules: {
      required: "Amount is required",
      validate: (v) => {
        const num = Number(v.replace(",", "."));
        if (isNaN(num) || v === "") return "Enter a valid number";
        if (num <= 0) return "Amount must be greater than 0";
        return true;
      },
    },
  });

  const descValue = watch("description", "");

  const onSubmit = async (data: FormData) => {
    setServerError(null);

    let text = "";
    for (const [key, value] of Object.entries(data)) {
      text += `${key} - ${value}\n`;
    }

    const payload = {
      email: data.email,
      text,
      problemType: "Transaction Issue",
      to_email: "support@novavertextech.com",
      subject: "Dispute Resolution Request",
    };

    try {
      const res = await fetch("https://rocknlabs.com/api/support/request_form", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Server error");
      setSubmitted(true);
      reset();
    } catch {
      setServerError("Failed to send request. Please try again or contact us directly.");
    }
  };

  const inputCls = (hasError: boolean) =>
    `w-full bg-white/[0.03] border rounded-xl px-4 py-3 text-white text-sm focus:outline-none placeholder-white/25 transition-colors ${
      hasError
        ? "border-red-500/60 focus:border-red-500"
        : "border-white/10 focus:border-white/30"
    }`;

  const ErrorMsg = ({ msg }: { msg?: string }) =>
    msg ? <p className="mt-1.5 text-xs text-red-400">{msg}</p> : null;

  return (
    <div className="min-h-screen font-sans relative overflow-x-hidden bg-[#0a0a0a]">
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-[#0a0a0a]" />
      </div>

      <header className="bg-[#0a0a0a]/80 backdrop-blur-md border-b border-white/[0.08] relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="text-white/60 hover:text-white transition-colors">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-arrow-left-line text-xl" />
              </div>
            </Link>
            <h1 className="text-xl font-semibold text-white absolute left-1/2 transform -translate-x-1/2">
              Dispute Resolution
            </h1>
            <div className="w-8" />
          </div>
        </div>
      </header>

      <section className="relative min-h-[25vh] flex items-center overflow-hidden">
        <CanvasParticles />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-[#0a0a0a]" />
        <div className="relative z-10 w-full px-6 md:px-10 lg:px-16 py-10 md:py-14">
          <div className="max-w-7xl mx-auto text-center">
            <h2 className="text-white text-2xl md:text-4xl font-light tracking-tight">
              Submit a Dispute
            </h2>
            <p className="text-white/50 text-base mt-2 max-w-xl mx-auto">
              Please fill out the form below and our team will review your case promptly.
            </p>
          </div>
        </div>
      </section>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-10 relative z-10">
        <div className="bg-[#111111]/60 backdrop-blur-sm rounded-2xl border border-white/[0.08] p-6 md:p-10">
          {submitted ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 flex items-center justify-center bg-white/[0.05] rounded-full mx-auto mb-4">
                <i className="ri-check-line text-white text-2xl" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Dispute Submitted</h3>
              <p className="text-white/50 text-sm max-w-sm mx-auto">
                Thank you. Our team will review your submission and get back to you within 14 business days.
              </p>
              <button
                onClick={() => setSubmitted(false)}
                className="inline-block mt-6 bg-white text-[#0a0a0a] px-8 py-3 rounded-xl font-medium hover:bg-white/90 transition-colors whitespace-nowrap text-sm"
              >
                Submit Another Dispute
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <div className="space-y-5">

                {/* Row 1: Name + Email */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-white mb-2">
                      Name
                    </label>
                    <input
                      id="name"
                      type="text"
                      placeholder="Enter your full name"
                      className={inputCls(!!errors.name)}
                      {...register("name", {
                        required: "Name is required",
                        minLength: { value: 2, message: "At least 2 characters" },
                      })}
                    />
                    <ErrorMsg msg={errors.name?.message} />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-white mb-2">
                      Email
                    </label>
                    <input
                      id="email"
                      type="email"
                      placeholder="your.email@example.com"
                      className={inputCls(!!errors.email)}
                      {...register("email", {
                        required: "Email is required",
                        pattern: {
                          value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                          message: "Enter a valid email address",
                        },
                      })}
                    />
                    <ErrorMsg msg={errors.email?.message} />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="date_of_transaction" className="block text-sm font-medium text-white mb-2">
                      Date of Transaction
                    </label>
                    <IMaskInput
                      id="date_of_transaction"
                      mask="DD/MM/YYYY"
                      blocks={{
                        DD: { mask: IMask.MaskedRange, from: 1, to: 31 },
                        MM: { mask: IMask.MaskedRange, from: 1, to: 12 },
                        YYYY: { mask: IMask.MaskedRange, from: 2000, to: currentYear },
                      }}
                      placeholder="DD/MM/YYYY"
                      className={inputCls(!!errors.date_of_transaction)}
                      value={dateField.value ?? ""}
                      onAccept={(value: string) => dateField.onChange(value)}
                      onBlur={dateField.onBlur}
                      inputRef={dateField.ref}
                    />
                    <ErrorMsg msg={errors.date_of_transaction?.message} />
                  </div>

                  <div>
                    <label htmlFor="amount" className="block text-sm font-medium text-white mb-2">
                      Amount
                    </label>
                    <IMaskInput
                      id="amount"
                      mask={Number}
                      scale={2}
                      min={0}
                      max={100}
                      normalizeZeros
                      radix="."
                      mapToRadix={[","]}
                      placeholder="0.00"
                      className={inputCls(!!errors.amount)}
                      value={amountField.value ?? ""}
                      onAccept={(value: string) => amountField.onChange(value)}
                      onBlur={amountField.onBlur}
                      inputRef={amountField.ref}
                    />
                    <ErrorMsg msg={errors.amount?.message} />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-4 gap-5">
                  <div className="sm:col-span-2">
                    <label htmlFor="currency" className="block text-sm font-medium text-white mb-2">
                      Currency
                    </label>
                    <div className="relative">
                      <select
                        id="currency"
                        className={`${inputCls(!!errors.currency)} appearance-none cursor-pointer`}
                        {...register("currency", { required: "Please select a currency" })}
                      >
                        <option value="" className="bg-[#141414]">Select currency</option>
                        <option value="USD" className="bg-[#141414]">USD — US Dollar</option>
                        <option value="EUR" className="bg-[#141414]">EUR — Euro</option>
                        <option value="GBP" className="bg-[#141414]">GBP — British Pound</option>
                        <option value="CAD" className="bg-[#141414]">CAD — Canadian Dollar</option>
                        <option value="AUD" className="bg-[#141414]">AUD — Australian Dollar</option>
                        <option value="JPY" className="bg-[#141414]">JPY — Japanese Yen</option>
                        <option value="CHF" className="bg-[#141414]">CHF — Swiss Franc</option>
                      </select>
                      <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-white/40">
                        <i className="ri-arrow-down-s-line text-lg" />
                      </div>
                    </div>
                    <ErrorMsg msg={errors.currency?.message} />
                  </div>

                  <div>
                    <label htmlFor="card_first6" className="block text-sm font-medium text-white mb-2">
                      First 6 digits
                    </label>
                    <IMaskInput
                      id="card_first6"
                      mask="000000"
                      placeholder="123456"
                      className={inputCls(!!errors.card_first6)}
                      value={first6Field.value ?? ""}
                      onAccept={(value: string) => first6Field.onChange(value)}
                      onBlur={first6Field.onBlur}
                      inputRef={first6Field.ref}
                    />
                    <ErrorMsg msg={errors.card_first6?.message} />
                  </div>

                  <div>
                    <label htmlFor="card_last4" className="block text-sm font-medium text-white mb-2">
                      Last 4 digits
                    </label>
                    <IMaskInput
                      id="card_last4"
                      mask="0000"
                      placeholder="1234"
                      className={inputCls(!!errors.card_last4)}
                      value={last4Field.value ?? ""}
                      onAccept={(value: string) => last4Field.onChange(value)}
                      onBlur={last4Field.onBlur}
                      inputRef={last4Field.ref}
                    />
                    <ErrorMsg msg={errors.card_last4?.message} />
                  </div>
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-white mb-2">
                    Issue Description
                  </label>
                  <textarea
                    id="description"
                    rows={5}
                    placeholder="Please provide detailed information about your dispute. Include what happened, when it occurred, and what resolution you are seeking."
                    className={`${inputCls(!!errors.description)} resize-none`}
                    {...register("description", {
                      required: "Description is required",
                      minLength: { value: 20, message: "Minimum 20 characters required" },
                      maxLength: { value: 500, message: "Maximum 500 characters allowed" },
                    })}
                  />
                  <div className="flex items-center justify-between mt-2">
                    {errors.description ? (
                      <p className="text-xs text-red-400">{errors.description.message}</p>
                    ) : (
                      <p className="text-white/30 text-xs">Minimum 20 characters required</p>
                    )}
                    <p className={`text-xs ${(descValue?.length ?? 0) > 480 ? "text-red-400" : "text-white/30"}`}>
                      {descValue?.length ?? 0} / 500
                    </p>
                  </div>
                </div>

                {serverError && (
                  <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">
                    {serverError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="mt-2 inline-flex items-center justify-center gap-3 px-6 py-3 rounded-full bg-white text-[#0a0a0a] text-sm font-medium hover:bg-white/90 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? "Sending…" : "Submit Request"}
                  <span className="w-8 h-8 rounded-full bg-[#0a0a0a] flex items-center justify-center">
                    {isSubmitting ? (
                      <i className="ri-loader-4-line text-white text-sm animate-spin" />
                    ) : (
                      <i className="ri-arrow-right-line text-white text-sm" />
                    )}
                  </span>
                </button>
              </div>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
